import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "../ProcessAndProcedure/MedicalReports/MedicalReports.scss";
import { UserContext } from "../../Context/UserContextAPI";
import "./PrintRegistration.scss";
import "../Investigation/Report/Certificates/HeldUp.scss";
import "../Investigation/Report/Certificates/Unfit.scss";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { calculateUserMedicalStatus } from "../../components/Users/calculateUserMedicalStatus";

const PrintPhotoRegistration = () => {
  const pdfRef = useRef();
  const { state } = useLocation();
  const { currentUser, settingsData } = useContext(UserContext);
  const [userDetails, setUserDetails] = useState([]);
  const [rowSelection, setRowSelection] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    status1: 0,
    status2: 0,
    status3: 0,
    status4: 0,
  });

  const now = new Date();
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(now);

  const getStatusColor = (status) => {
    switch (status) {
      case 2:
        return "success";
      case 3:
        return "danger";
      case 4:
        return "primary";
      default:
        return "dark"; // You can set a default color if needed
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 2:
        return "Fit";
      case 3:
        return "Unfit";
      case 4:
        return "Held-up";
      default:
        return "Processing"; // You can set a default text if needed
    }
  };

  const calculateStatus = (data) => {
    const counts = {
      status1: 0,
      status2: 0,
      status3: 0,
      status4: 0,
    };

    data.forEach((userInfo) => {
      switch (userInfo?.user?.status) {
        case 1:
          // console.log(userInfo?.user?.status);
          counts.status1 = counts.status1 + 1;
          break;
        case 2:
          // console.log(userInfo?.user?.status);
          counts.status2 = counts.status2 + 1;
          break;
        case 3:
          // console.log(userInfo?.user?.status);
          counts.status3 = counts.status3 + 1;
          break;
        case 4:
          // console.log(userInfo?.user?.status);
          counts.status4 = counts.status4 + 1;
          break;
        default:
          break;
      }
    });

    setStatusCounts(counts);
  };

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("SL.", {
        header: () => (
          <div className="d-flex justify-content-center">
            <span className="text-center px-2">SL</span>
          </div>
        ),
        cell: (row) => {
          return (
            <div className="text-center d-flex justify-content-center fw-bold">
              <span className="text-center">{row?.row?.index + 1}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: () => (
          <span className="d-flex justify-content-center">
            Date
            <br />
            Invoice Number
          </span>
        ),
        cell: (info, index) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.createdAt ? (
                <>{`${info.row.original.createdAt.split("T")[0]}`}</>
              ) : (
                "---"
              )}

              {info?.row?.original?.registration_no ? (
                <>
                  <br />
                  {`${info?.row?.original?.reg_prefix || ""}${
                    info?.row?.original?.registration_no
                  }`}{" "}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("user.photo", {
        header: () => (
          <span className="d-flex justify-content-center">Photo</span>
        ),
        cell: (info, index) => {
          return (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ width: "80px" }}
            >
              {info?.row?.original?.image ? (
                <img
                  src={`${process.env.REACT_APP_UPLOAD_URL}/users/${info?.row?.original?.image}`}
                  className="img-fluid object-fit-contain mx-auto"
                  style={{ width: "90px", objectFit: "fill" }}
                  alt="patient"
                />
              ) : (
                <img
                  src="./dummy.jpg"
                  alt="patient"
                  className="img-fluid object-fit-contain mx-auto"
                  style={{ width: "90px", objectFit: "contain" }}
                />
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("user.registration_no", {
        header: () => (
          <span className="d-flex justify-content-center">
            Reg. No
            <br />
            Name
          </span>
        ),
        cell: (info, index) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.registration_no ? (
                <>
                  {`${info?.row?.original?.reg_prefix || ""}${
                    info?.row?.original?.registration_no
                  }`}{" "}
                </>
              ) : (
                "---"
              )}

              {info?.row?.original?.f_name ? (
                <>
                  <br />
                  {`${info.row.original.f_name}`}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("user.passport_no", {
        header: () => (
          <span className="d-flex justify-content-center">
            Passport No
            <br />
            DOB
          </span>
        ),
        cell: (info, index) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.passport_no ? (
                <>{`${info.row.original.passport_no}`}</>
              ) : (
                "---"
              )}

              {info?.row?.original?.date_of_birth ? (
                <>
                  <br />
                  {`${info.row.original.date_of_birth.split("T")[0]}`}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("introducer_user.f_name", {
        header: () => (
          <span
            className="d-flex justify-content-center
        "
          >
            Father's Name
            <br />
            Introducer Name
            <br />
            M.R. Name
          </span>
        ),
        cell: (info, index) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.userDetail?.father_name ? (
                <>{`${info.row.original?.userDetail?.father_name}`}</>
              ) : (
                "---"
              )}

              {info?.row?.original?.userDetail?.introducer_user?.f_name ? (
                <>
                  <br />{" "}
                  {`${info.row.original?.userDetail?.introducer_user?.f_name}`}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}

              {info?.row?.original?.userDetail?.introducer_user
                ?.user_users_maps[0]?.representative_user_map?.f_name ? (
                <>
                  <br />
                  {`${info.row.original?.userDetail?.introducer_user?.user_users_maps[0]?.representative_user_map?.f_name}`}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("country.shortname", {
        header: () => (
          <span className="d-flex justify-content-center">
            Country
            <br />
            Company
          </span>
        ),
        cell: (info, index) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.userDetail?.country?.name ? (
                <>{`${info.row.original?.userDetail?.country?.name}`}</>
              ) : (
                "---"
              )}

              {info?.row?.original?.userDetail?.company_user?.f_name ? (
                <>
                  <br />
                  {`${info.row.original?.userDetail?.company_user?.f_name}`}
                </>
              ) : (
                <>
                  <br />
                  {"---"}
                </>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("userDetail.package.name", {
        id: "package",
        header: () => (
          <span className="text-center d-flex justify-content-center">
            Package
          </span>
        ),
        cell: (info) => {
          const userTest = info?.row?.original?.userTest || [];
          // console.log("Investigation Code:", userTest);
          const userTestInvestigation = userTest
            ?.map((test) => test?.investigation?.code)
            .join(", ");
          // console.log('Investigation Code:', userTestInvestigation);

          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.role_id === 5 ? (
                <span>{userTestInvestigation}</span>
              ) : (
                info.getValue()
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("user.status", {
        header: () => (
          <span className="d-flex justify-content-center">
            Status
            <br />
            Remarks
          </span>
        ),
        cell: (info, index) => {
          const comments = info?.row?.original?.user_test_comments
            ? info?.row?.original?.user_test_comments[0]?.comments
            : "";

          const { isDone, status } = calculateUserMedicalStatus(
            info?.row?.original,
          );

          let content;

          // there was done
          if (isDone && status === 2) {
            content = "FIT";
          } else if (isDone && status === 3) {
            content = "UNFIT";
          } else if (isDone && status === 4) {
            content = "HELD-UP";
          } else {
            content = "PROCESSING";
          }

          return (
            <div className="d-flex justify-content-start">
              <span
                className={`text-left ${getStatusColor(
                  info?.row?.original?.user?.status,
                )}`}
              >
                {content}
                {comments ? (
                  <>
                    Pathology:{" "}
                    <span dangerouslySetInnerHTML={{ __html: comments }} />
                  </>
                ) : (
                  ""
                )}
              </span>
            </div>
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: state?.checkedUsers,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleGeneratePDF = async () => {
    const htmlContent = pdfRef.current.outerHTML;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user-details/generate-pdf`,
        { htmlContent },
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `Medical-Registration-${
        state?.searchedData?.fromDate ?? ""
      } ${state?.searchedData?.toDate ? "To" : ""} ${
        state?.searchedData?.toDate ?? ""
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL object after download
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF", error);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      handleGeneratePDF();
    }, 1200);
  }, []);

  return (
    <>
      <div className="card medical-report-container">
        <div className="card-body">
          <div className="rounded">
            <div className="print-reg-header">
              <div className="card shadow-none mb-3">
                <div className="border p-3 rounded">
                  {state?.searchedData?.introducer ? (
                    <h6 className="mb-0 text-uppercase text-center fw-bold">
                      {state?.searchedData?.introducer?.label}{" "}
                    </h6>
                  ) : (
                    <h6 className="mb-0 text-uppercase text-center fw-bold text-dark">
                      All Registration
                    </h6>
                  )}
                  <div className="mb-0 text-center fw-light">
                    From{" "}
                    {state?.searchedData?.from_date
                      ? format(
                          new Date(state?.searchedData?.from_date),
                          "dd-MMM-yyyy",
                          { locale: enUS }, // or use es for Spanish, adjust as needed
                        )
                      : format(new Date(), "dd-MMM-yyyy", {
                          locale: enUS,
                        })}{" "}
                    To{" "}
                    {state?.searchedData?.to_date
                      ? format(
                          new Date(state?.searchedData?.to_date),
                          "dd-MMM-yyyy",
                          { locale: enUS }, // or use es for Spanish, adjust as needed
                        )
                      : format(new Date(), "dd-MMM-yyyy", { locale: enUS })}
                  </div>
                </div>
              </div>
            </div>

            <table
              className="table align-middle table-hover table-bordered mb-0 rounded p-1"
              style={{ fontSize: "11px" }}
              ref={pdfRef}
              // id="mytable"
            >
              <thead className="bg-primary">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, i) => (
                      <th
                        key={i}
                        id={`th-${i}`}
                        className="text-light p-1"
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, i) => {
                  const { isDone, status } = calculateUserMedicalStatus(
                    row?.original,
                  );

                  const rowClass = (() => {
                    if (status === 2) {
                      return isDone ? "text-success" : "";
                    } else if (status === 3) {
                      return isDone ? "text-danger" : "";
                    } else if (status === 4) {
                      return isDone ? "text-primary" : "";
                    } else {
                      return "text-dark";
                    }
                  })();
                  return (
                    <tr key={i} className={rowClass}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="p-1">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="print-reg-footer">
          <div className="d-flex flex-column align-items-center justify-content-center pb-3 text-dark">
            <div>
              Created at {formattedDate} by {currentUser?.f_name}
            </div>
            <div>
              Copyright &copy; {new Date().getFullYear()}{" "}
              {settingsData?.website_name}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintPhotoRegistration;
